/**
 * DiscountSellingPlanSettingsManager handles the retrieval, parsing, and storage of discount and selling plan settings
 * from URL query parameters or cookies. It fetches custom discount and selling plan settings from the Shopify store
 * and stores the settings in cookies for subsequent usage.
 */
class DiscountSellingPlanSettingsManager {
  /**
   * The metaobject type used in fetching discount and selling plan settings.
   * @type {string}
   */
  metaType = 'custom_discount_selling_plan_settings';

  /**
   * Helper method to parse and validate the JSON string from the query parameter.
   *
   * @param {string} jsonString - The JSON string to parse.
   * @returns {DiscountSellingPlanQueryPayload} - Custom Discount & Selling Plan Query Payload.
   * @throws {Error} - Throws an error if the JSON is invalid or validation fails.
   */
  parseQueryPayload(jsonString) {
    try {
      const queryPayload = JSON.parse(jsonString);

      if (
        typeof queryPayload.handle !== 'string' ||
        typeof queryPayload.storeName !== 'string' ||
        typeof queryPayload.accessToken !== 'string'
      ) {
        throw new Error(`Invalid object structure: ${JSON.stringify(queryPayload)}`);
      }

      return queryPayload;
    } catch (error) {
      throw new Error(`Failed to parse or validate JSON: ${error.message}`);
    }
  }

  /**
   * Retrieves the query parameter payload from the current URL, decodes it, and validates its structure.
   *
   * @returns {DiscountSellingPlanQueryPayload|null} - The parsed query payload or null if an error occurs.
   */
  getQueryParamPayload() {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const d = searchParams.get('d');
      if (d) {
        const jsonString = atob(d);
        const queryPayload = this.parseQueryPayload(jsonString);
        return queryPayload;
      }
    } catch (error) {
      console.error('DiscountSellingPlanSettingsManager', 'Error Extracting Data from Query:', error);
    }
    return null;
  }

  /**
   * Fetches the discount and selling plan settings based on the query parameters, and saves them to a cookie.
   *
   * @returns {Promise<void>} - A promise that resolves when the settings are successfully saved.
   */
  async scanAndSaveDiscountSellingPlanSettings() {
    const queryPayload = this.getQueryParamPayload();
    if (queryPayload) {
      const storefrontClient = new StorefrontClient(queryPayload.storeName, queryPayload.accessToken);
      const discountSellingPlanSettings = await storefrontClient.fetchCustomDiscountSellingPlanSettings(
        this.metaType,
        queryPayload.handle
      );

      const dayInMillis = 24 * 60 * 60 * 1000;
      setCookie('_rog_d_sp', btoa(JSON.stringify(discountSellingPlanSettings)), dayInMillis);
    }
    this.updateSubscriptionsVisibility();
  }

  /**
   * Updates the visibility of subscription variants.
   * This method identifies and processes all subscription sub-variants, groups them by
   * variant ID and selling plan frequency, and applies visibility logic based on the discount
   * and selling plan settings. Sub-variants that are not selected are hidden from view.
   *
   * It also automatically triggers the click event on the first sub-variant in display.
   *
   * @returns {void} - This method does not return a value.
   */
  updateSubscriptionsVisibility() {
    const plpSubscribes = document.querySelectorAll('.plp-subscribe');

    plpSubscribes.forEach((plpSubscribe) => {
      const plpSubVariantsContainers = plpSubscribe.querySelectorAll(
        '.plp-sub__recurring .plp-sub__variants-container'
      );

      plpSubVariantsContainers.forEach((plpSubVariantsContainer) => {
        const subVariants = plpSubVariantsContainer.querySelectorAll('.plp-sub__variant');

        /**
         * An array of extended sub-variant objects extracted from DOM elements.
         *
         * @type {Array<ExtendedSubVariant>}
         */
        const allExtendedSubVariants = [...subVariants].map((subVariant) => {
          const variantDataset = DiscountSellingPlanSettingsManager.extractDatasetFromVariantElem(subVariant);

          const extended = {
            node: subVariant,
            variantDataset,
          };

          return extended;
        });

        const extendedSubVariantsInDisplay = [];

        /**
         * Groups the extended subscription variants by variant ID and selling plan frequency.
         *
         * @type {GroupedExtendedSubVariantsCollection}
         */
        const groupedExtendedSubVariantsCollection = allExtendedSubVariants.reduce((acc, extendedSubVariants) => {
          const { variantId, sellingPlanFrequency } = extendedSubVariants.variantDataset;
          const groupKey = `${variantId}-${sellingPlanFrequency}`;
          if (!acc[groupKey]) acc[groupKey] = [];
          acc[groupKey].push(extendedSubVariants);
          return acc;
        }, {});

        const discountSellingPlanSettings = DiscountSellingPlanSettingsManager.loadDiscountSellingPlanSettings();

        let extendedSubVariantToDisplay = null;
        Object.keys(groupedExtendedSubVariantsCollection).forEach((groupCollectionKey) => {
          const groupedExtendedSubVariants = groupedExtendedSubVariantsCollection[groupCollectionKey];

          const displayCandidate = groupedExtendedSubVariants.reduce((min, extendedSubVariant) => {
            const allocationPrice = parseFloat(extendedSubVariant.variantDataset.sellingPlanAllocationPrice) || 0;
            return allocationPrice <= (min ? parseFloat(min.variantDataset.sellingPlanAllocationPrice) : Infinity)
              ? extendedSubVariant
              : min;
          }, null);

          extendedSubVariantToDisplay = displayCandidate;

          const customDiscounts = discountSellingPlanSettings?.customDiscounts || null;
          const customSellingPlans = discountSellingPlanSettings?.customSellingPlans || null;

          if (customSellingPlans?.length || customDiscounts?.length) {
            for (const extendedSubVariant of groupedExtendedSubVariants) {
              const variantDataset = extendedSubVariant.variantDataset;

              let customDiscount = null;
              if (customDiscounts?.length) {
                customDiscount = DiscountSellingPlanSettingsManager.getTargetedDiscount(
                  variantDataset.variantId,
                  variantDataset.sellingPlanId,
                  customDiscounts
                );

                if (customDiscount) {
                  const sellingPlanAllocationCompareAtPrice = Number(
                    variantDataset?.sellingPlanAllocationCompareAtPrice || '0'
                  );
                  const sellingPlanAllocationPrice = Number(variantDataset?.sellingPlanAllocationPrice || '0');
                  const couponCodePercentage = Number(customDiscount?.couponCodePercentage || '0') / 100;

                  const finalPrice = Math.ceil(sellingPlanAllocationPrice * (1 - couponCodePercentage));
                  const finalPercentage = (1 - finalPrice / sellingPlanAllocationCompareAtPrice) * 100;
                  const finalRoundedPercentage = DiscountSellingPlanSettingsManager.roundToNearestFive(finalPercentage);

                  const badgeElem = extendedSubVariant.node.querySelector('.plp-sub__variant_save-badge strong');

                  if (badgeElem) badgeElem.textContent = `Save ${finalRoundedPercentage}%`;
                  const finalPriceElem = last(
                    extendedSubVariant.node.querySelectorAll('.plp-sub__variant_sub-title:not(:has(.oos-message)) strong')
                  );
                  if (finalPriceElem)
                    finalPriceElem.textContent = `${DiscountSellingPlanSettingsManager.formatMoney(finalPrice)}`;

                  if (customDiscount?.exclusiveBanner) {
                    const sellingPlanAllocationCompareAtPrice = Number(
                      variantDataset?.sellingPlanAllocationCompareAtPrice || '0'
                    );
                    const sellingPlanAllocationPrice = Number(variantDataset?.sellingPlanAllocationPrice || '0');
                    const couponCodePercentage = Number(customDiscount?.couponCodePercentage || '0') / 100;

                    const finalPriceValue = Math.ceil(sellingPlanAllocationPrice * (1 - couponCodePercentage));
                    const finalPercentageValue = (1 - finalPriceValue / sellingPlanAllocationCompareAtPrice) * 100;

                    const totalPercentage = `${DiscountSellingPlanSettingsManager.roundToNearestFive(
                      finalPercentageValue
                    )}%`;
                    const frequency = variantDataset.sellingPlanFrequency
                      .toLocaleLowerCase()
                      .replace('delivery every ', '')
                      .replaceAll(' ', '-')
                      .replace(/s$/, '');

                    const strikethroughPrice = DiscountSellingPlanSettingsManager.formatMoney(
                      sellingPlanAllocationCompareAtPrice
                    );
                    const finalPrice = DiscountSellingPlanSettingsManager.formatMoney(finalPriceValue);
                    const sellingPlanPercentage = `${variantDataset.sellingPlanCheckoutChargePriceAdjustmentsValue}%`;

                    let discountTitle = customDiscount.exclusiveBanner.title;
                    let discountHeading = customDiscount.exclusiveBanner.heading;
                    let discountSubheading = customDiscount.exclusiveBanner.subheading;
                    let discountSubtext = customDiscount.exclusiveBanner.subtext;
                    let discountCartDiscountText = customDiscount.exclusiveBanner.cartDiscountText;

                    const replacements = {
                      totalPercentage,
                      frequency,
                      strikethroughPrice,
                      finalPrice,
                      sellingPlanPercentage,
                    };

                    discountTitle = DiscountSellingPlanSettingsManager.templateTextParsing(discountTitle, replacements);
                    discountHeading = DiscountSellingPlanSettingsManager.templateTextParsing(
                      discountHeading,
                      replacements
                    );
                    discountSubheading = DiscountSellingPlanSettingsManager.templateTextParsing(
                      discountSubheading,
                      replacements,
                      true
                    );
                    discountSubtext = DiscountSellingPlanSettingsManager.templateTextParsing(
                      discountSubtext,
                      replacements
                    );
                    discountCartDiscountText = DiscountSellingPlanSettingsManager.templateTextParsing(
                      discountCartDiscountText,
                      replacements
                    );

                    const exclusiveOfferBanner = `
                      <div class="plp-sub__exclusive-offer-banner">
                        <span class="plp-sub__exclusive-offer-icon">
                          <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor">
                            </path>
                          </svg>
                        </span>

                        <div class="plp-sub__exclusive-offer-container">
                          <span class="plp-sub__exclusive-offer-title">${discountTitle}</span>
                          <span class="plp-sub__exclusive-offer-heading">${discountHeading}</span>
                          <span class="plp-sub__exclusive-offer-subheading">${discountSubheading}</span>
                          <span class="plp-sub__exclusive-offer-subtext">${discountSubtext}</span>
                        </div>
                      </div>
                    `;

                    const plpSubExclusiveOfferBannersList = plpSubscribe.querySelector(
                      '.plp-sub__exclusive-offer-banners-list'
                    );
                    plpSubExclusiveOfferBannersList.insertAdjacentHTML('beforeend', exclusiveOfferBanner);
                  }
                }
              }

              if (customSellingPlans?.length) {
                const customSellingPlan = customSellingPlans.find(({ variantId, sellingPlanId }) => {
                  if (variantId == variantDataset.variantId && sellingPlanId == variantDataset.sellingPlanId) {
                    return true;
                  }
                  return false;
                });
                if (customSellingPlan) {
                  extendedSubVariantToDisplay = extendedSubVariant;
                }
              }
            }
          }

          extendedSubVariantsInDisplay.push(extendedSubVariantToDisplay);
          groupedExtendedSubVariants.forEach((extendedSubVariant) => {
            if (
              extendedSubVariant.variantDataset.sellingPlanId !==
              extendedSubVariantToDisplay.variantDataset.sellingPlanId
            ) {
              extendedSubVariant.node.classList.add('hidden');
            }
          });
        });

        if (extendedSubVariantsInDisplay.length && !plpSubscribe.classList.contains('edit-subscribe')) {
          setTimeout(() => {
            const firstAvailableVariant = extendedSubVariantsInDisplay.find(item => !item.node.attributes['data-oos']).node;
            plpSubscribe.updateVariantInputs({ currentTarget: firstAvailableVariant });
          }, 200);
        }

        plpSubVariantsContainer.setAttribute('aria-hidden', 'false');
      });
    });
  }

  /**
   * Loads discount and selling plan settings from a cookie.
   *
   * @returns {CustomDiscountSellingPlanSettings|null} - The discount and selling plan settings or null if not available.
   */
  static loadDiscountSellingPlanSettings() {
    const cookieData = getCookie('_rog_d_sp');
    if (cookieData) {
      try {
        const discountSellingPlanSettings = JSON.parse(atob(cookieData));
        return discountSellingPlanSettings;
      } catch (error) {
        console.error('DiscountSellingPlanSettingsManager', 'Error Loading Data from Cookie:', error);
      }
    }
    return null;
  }

  /**
   * Retrieves the custom discount applicable to a specific variant and optionally a selling plan.
   *
   * @param {string} [variantId=''] - The ID of the product variant to search for discounts.
   * @param {string} [sellingPlanId=''] - The ID of the selling plan to search for discounts (optional).
   * @param {CustomDiscount[]|undefined} customDiscounts - The list of custom discounts.
   * @returns {CustomDiscount|null} - The applicable custom discount for the given variant and selling plan,
   *                                  or null if no discount is found.
   */
  static getTargetedDiscount(variantId = '', sellingPlanId = '', customDiscounts = undefined) {
    if (!customDiscounts) {
      const discountSellingPlanSettings = DiscountSellingPlanSettingsManager.loadDiscountSellingPlanSettings();
      customDiscounts = discountSellingPlanSettings?.customDiscounts || undefined;
    }

    let targetedDiscount = null;
    if (customDiscounts) {
      for (const customDiscount of customDiscounts) {
        if (
          (!sellingPlanId && customDiscount.variantId == variantId && !customDiscount.eligibleSellingPlanId) ||
          (sellingPlanId &&
            customDiscount.variantId == variantId &&
            customDiscount.eligibleSellingPlanId == sellingPlanId)
        ) {
          targetedDiscount = customDiscount;
        }
      }
    }

    return targetedDiscount;
  }

  /**
   * Rounds a given number to the nearest multiple of 5.
   * @param {number} num - The number to be rounded.
   * @returns {number} - The number rounded to the nearest multiple of 5.
   */
  static roundToNearestFive(num) {
    return Math.round(num / 5) * 5;
  }

  /**
   * Formats a number representing cents into a money string.
   *
   * @param {number} amount - The amount in cents.
   * @returns {string} The formatted money string in the format "$X.XX".
   *
   * @example
   * const moneyString = formatMoney(7663); // "$76.63"
   */
  static formatMoney(amount) {
    const dollars = (amount / 100).toFixed(2); // Convert cents to dollars and ensure two decimal places
    return `$${dollars}`;
  }

  /**
   * Updates the exclusive offer banner with the appropriate discount information based on the selected variant
   * and selling plan in the subscription container.
   *
   * This function finds the selected subscription option, retrieves relevant data from the selected variant,
   * and then updates the exclusive offer banner with calculated discount details such as the total percentage
   * discount, frequency of the subscription, and final prices. The banner is hidden if no applicable discount
   * or selling plan is found.
   *
   * @param {HTMLElement} subscriptionContainer - The container element for subscription options that holds
   *                                              the input and offer banner elements.
   *
   * @example
   * const container = document.querySelector('.subscription-container');
   * DiscountSellingPlanSettingsManager.updateExclusiveOfferBanner(container);
   */
  static updateExclusiveOfferBanner(subscriptionContainer) {
    if (!subscriptionContainer) return;

    const exclusiveOfferBanner = subscriptionContainer.querySelector('.plp-sub__exclusive-offer-banner');
    if (!exclusiveOfferBanner) return;

    const radioSelector = subscriptionContainer?.querySelector('input[type="radio"]:checked');
    const subOptionsElem = radioSelector?.closest('.plp-sub__options');

    const selectedVariant = subOptionsElem?.querySelector('.plp-sub__selected-variant');
    if (!selectedVariant) return;

    const variantDataset = DiscountSellingPlanSettingsManager.extractDatasetFromVariantElem(selectedVariant);

    const variantId = variantDataset.variantId;
    const sellingPlanId = variantDataset.sellingPlanId;

    if (sellingPlanId && sellingPlanId != '0') {
      const customDiscount = DiscountSellingPlanSettingsManager.getTargetedDiscount(variantId, sellingPlanId);
      if (customDiscount && customDiscount.exclusiveBanner) {
        const sellingPlanAllocationCompareAtPrice = Number(variantDataset?.sellingPlanAllocationCompareAtPrice || '0');
        const sellingPlanAllocationPrice = Number(variantDataset?.sellingPlanAllocationPrice || '0');
        const couponCodePercentage = Number(customDiscount?.couponCodePercentage || '0') / 100;

        const finalPriceValue = Math.ceil(sellingPlanAllocationPrice * (1 - couponCodePercentage));
        const finalPercentageValue = (1 - finalPriceValue / sellingPlanAllocationCompareAtPrice) * 100;

        const totalPercentage = `${DiscountSellingPlanSettingsManager.roundToNearestFive(finalPercentageValue)}%`;
        const frequency = variantDataset.sellingPlanFrequency
          .toLocaleLowerCase()
          .replace('delivery every ', '')
          .replaceAll(' ', '-')
          .replace(/s$/, '');

        const strikethroughPrice = DiscountSellingPlanSettingsManager.formatMoney(sellingPlanAllocationCompareAtPrice);
        const finalPrice = DiscountSellingPlanSettingsManager.formatMoney(finalPriceValue);
        const sellingPlanPercentage = `${variantDataset.sellingPlanCheckoutChargePriceAdjustmentsValue}%`;

        let discountTitle = customDiscount.exclusiveBanner.title;
        let discountHeading = customDiscount.exclusiveBanner.heading;
        let discountSubheading = customDiscount.exclusiveBanner.subheading;
        let discountSubtext = customDiscount.exclusiveBanner.subtext;
        let discountCartDiscountText = customDiscount.exclusiveBanner.cartDiscountText;

        const replacements = {
          totalPercentage,
          frequency,
          strikethroughPrice,
          finalPrice,
          sellingPlanPercentage,
        };

        discountTitle = DiscountSellingPlanSettingsManager.templateTextParsing(discountTitle, replacements);
        discountHeading = DiscountSellingPlanSettingsManager.templateTextParsing(discountHeading, replacements);
        discountSubheading = DiscountSellingPlanSettingsManager.templateTextParsing(
          discountSubheading,
          replacements,
          true
        );
        discountSubtext = DiscountSellingPlanSettingsManager.templateTextParsing(discountSubtext, replacements);
        discountCartDiscountText = DiscountSellingPlanSettingsManager.templateTextParsing(
          discountCartDiscountText,
          replacements
        );

        exclusiveOfferBanner.querySelector('.plp-sub__exclusive-offer-title').innerHTML = discountTitle;
        exclusiveOfferBanner.querySelector('.plp-sub__exclusive-offer-heading').innerHTML = discountHeading;
        exclusiveOfferBanner.querySelector('.plp-sub__exclusive-offer-subheading').innerHTML = discountSubheading;
        exclusiveOfferBanner.querySelector('.plp-sub__exclusive-offer-subtext').innerHTML = discountSubtext;

        exclusiveOfferBanner.setAttribute('aria-hidden', false);
        return;
      }
    }
    exclusiveOfferBanner.setAttribute('aria-hidden', true);
  }

  /**
   * Extracts dataset attributes from a given variant element and returns them in an object.
   *
   * @param {HTMLElement} variantElem - The HTML element representing the variant, containing dataset attributes.
   *
   * @returns {VariantDataset} An object containing variant dataset properties.
   */
  static extractDatasetFromVariantElem(variantElem) {
    const productId = variantElem?.dataset?.productId || '';
    const variantId = variantElem?.dataset?.variantId || '';
    const variantPrice = variantElem?.dataset?.variantPrice || '';
    const variantCompereAtPrice = variantElem?.dataset?.variantCompereAtPrice || '';
    const sellingPlanId = variantElem?.dataset?.sellingPlanId || '';
    const sellingPlanName = variantElem?.dataset?.sellingPlanName || '';
    const sellingPlanFrequency = variantElem?.dataset?.sellingPlanFrequency || '';

    const sellingPlanCheckoutChargePriceAdjustmentsValue =
      variantElem?.dataset?.sellingPlanCheckoutChargePriceAdjustmentsValue || '';
    const sellingPlanCheckoutChargePriceAdjustmentsValueType =
      variantElem?.dataset?.sellingPlanCheckoutChargePriceAdjustmentsValueType || '';
    const sellingPlanAllocationPrice = variantElem?.dataset?.sellingPlanAllocationPrice || '';
    const sellingPlanAllocationCompareAtPrice = variantElem?.dataset?.sellingPlanAllocationCompareAtPrice || '';

    const variantDataset = {
      productId,
      variantId,
      variantPrice,
      variantCompereAtPrice,
      sellingPlanId,
      sellingPlanName,
      sellingPlanFrequency,
      sellingPlanCheckoutChargePriceAdjustmentsValue,
      sellingPlanCheckoutChargePriceAdjustmentsValueType,
      sellingPlanAllocationPrice,
      sellingPlanAllocationCompareAtPrice,
    };

    return variantDataset;
  }

  /**
   * Parses a template string and replaces placeholders with actual values.
   * This method supports dynamic replacements for the frequency, prices, and percentages
   * used in discount banners, including capitalization of frequency if specified.
   *
   * @param {string} template - The template string containing placeholders.
   * @param {Replacements} replacements - An object containing replacement values for the placeholders.
   * @param {boolean} [capitalizeFrequency=false] - Whether to capitalize the frequency value in the template.
   * @returns {string} - The parsed template string with the placeholders replaced by actual values.
   */
  static templateTextParsing(template, replacements, capitalizeFrequency = false) {
    const { totalPercentage, frequency, strikethroughPrice, finalPrice, sellingPlanPercentage } = replacements;

    const parsedFrequency = capitalizeFrequency
      ? frequency
          .toLowerCase()
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join('-')
      : frequency;

    let result = template
      .replace('{{total_percentage}}', totalPercentage)
      .replace('{{frequency}}', parsedFrequency)
      .replace(
        '{{strikethrough_price}}',
        `<span class="plp-sub__exclusive-offer-strikethrough-price">${strikethroughPrice}</span>`
      )
      .replace('{{final_price}}', finalPrice)
      .replace('{{selling_plan_percentage}}', sellingPlanPercentage);

    result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return result;
  }

  /**
   * Builds the replacements object based on the variant data and selling plan information.
   *
   * @param {string} variantId - The ID of the product variant.
   * @param {string} sellingPlanId - The ID of the selling plan.
   * @param {number|string} sellingPlanAllocationCompareAtPrice - The original price of the variant under the selling plan.
   * @param {number|string} sellingPlanAllocationPrice - The discounted price under the selling plan.
   * @param {string} sellingPlanFrequency - The frequency of the selling plan (e.g., 'delivery every month').
   * @param {string} sellingPlanCheckoutChargePriceAdjustmentsValue - The percentage discount or adjustment value for the selling plan.
   *
   * @returns {Object} - The replacements object containing the parsed and formatted data.
   */
  static buildReplacementsObject({
    variantId = '',
    sellingPlanId = '',
    sellingPlanAllocationCompareAtPrice = '0',
    sellingPlanAllocationPrice = '0',
    sellingPlanFrequency = '',
    sellingPlanCheckoutChargePriceAdjustmentsValue = '',
    customDiscount = {},
  }) {
    if (!variantId || !sellingPlanId || sellingPlanId === '0') return null;

    const compareAtPrice = Number(sellingPlanAllocationCompareAtPrice);
    const couponCodePercentage = Number(customDiscount?.couponCodePercentage || '0') / 100;

    const finalPriceValue = Math.ceil(sellingPlanAllocationPrice * (1 - couponCodePercentage));
    const finalPercentageValue = (1 - finalPriceValue / compareAtPrice) * 100;
    const totalPercentage = `${DiscountSellingPlanSettingsManager.roundToNearestFive(finalPercentageValue)}%`;

    const frequency = sellingPlanFrequency
      .toLocaleLowerCase()
      .replace('delivery every ', '')
      .replaceAll(' ', '-')
      .replace(/s$/, '');

    const strikethroughPrice = DiscountSellingPlanSettingsManager.formatMoney(compareAtPrice);
    const finalPrice = DiscountSellingPlanSettingsManager.formatMoney(finalPriceValue);
    const sellingPlanPercentage = `${sellingPlanCheckoutChargePriceAdjustmentsValue}%`;

    return {
      totalPercentage,
      frequency,
      strikethroughPrice,
      finalPrice,
      sellingPlanPercentage,
    };
  }
}

// JSDoc type definitions ------------------------------------------------------------------------------------------------

/**
 * @typedef {Object} DiscountSellingPlanQueryPayload
 * @property {string} handle - The handle of the entry.
 * @property {string} storeName - The name of the store associated with this entry.
 * @property {string} accessToken - The access token used for authentication.
 */

/**
 * @typedef {Object} ExtendedSubVariant
 * @property {HTMLElement} node - The DOM node representing the sub-variant element.
 * @property {VariantDataset} variantDataset - Parsed object of the VariantDataset
 */

/**
 * An object that groups extended sub-variants by their variant ID and selling plan frequency.
 *
 * @typedef {Object.<string, Array<ExtendedSubVariant>>} GroupedExtendedSubVariantsCollection
 * @property {string} key - A unique group key, which is a combination of `variantId` and `sellingPlanFrequency`.
 * @property {Array<ExtendedSubVariant>} value - An array of sub-variants that share the same `variantId` and `sellingPlanFrequency`.
 */

/**
 * @typedef {Object} VariantDataset
 * @property {string} productId - The ID of the product.
 * @property {string} variantId - The ID of the variant.
 * @property {string} variantPrice - The price of the variant.
 * @property {string} variantCompereAtPrice - The compare-at price of the variant.
 * @property {string} sellingPlanId - The ID of the selling plan.
 * @property {string} sellingPlanName - The name of the selling plan.
 * @property {string} sellingPlanFrequency - The frequency of the selling plan.
 * @property {string} sellingPlanCheckoutChargePriceAdjustmentsValue - The price adjustment value for the selling plan at checkout.
 * @property {string} sellingPlanCheckoutChargePriceAdjustmentsValueType - The type of the price adjustment at checkout.
 * @property {string} sellingPlanAllocationPrice - The allocated price of the selling plan.
 * @property {string} sellingPlanAllocationCompareAtPrice - The allocated compare-at price of the selling plan.
 */

/**
 * @typedef {Object} Replacements
 * @property {string} totalPercentage - The total discount percentage, formatted as a string (e.g., "20%").
 * @property {string} frequency - The frequency of the subscription (e.g., "every-month", "every-week").
 * @property {string} strikethroughPrice - The original price, formatted as a strikethrough price (e.g., "$100").
 * @property {string} finalPrice - The discounted final price, formatted as a string (e.g., "$80").
 * @property {string} sellingPlanPercentage - The selling plan discount percentage (e.g., "25%").
 */

/**
 * Returns the last element of an array.
 *
 * @param {Array} arr - The array from which to retrieve the last element.
 * @returns {*} The last element of the array, or undefined if the array is empty.
 */
const last = (arr) => arr[arr.length - 1];
